import React, { useEffect, useState, useRef } from "react";
import styles from "./MyAccount.module.css";
import AccountDataService from "../../Services/accountDataService";
import { useParameters } from "../../Context/ParameterContext";
import ChangeUsernameModal from "../../Components/Modal/ChangeUsernameModal";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import ChangePasswordModal from "../../Components/Modal/ChangePasswordModal";
import { useModal } from "../../Context/ModalContext";
import AccountService from "../../Services/accountService";
import i18n from "../../Services/i18n";
import { useToasts } from "../../LayoutComponents/ToastContext";
import PictureUploadField from "../../Components/PictureUploadField/PictureUploadField";
import RichEditor from "../../../src/Components/RichEditor/RichEditor";
import PlanComparisonModal from "../../Components/Modal/PlanComparaisonModal";
import envConfig from "../../config";
import ServiceHelper from "../../Services/serviceHelper";
import axios from "axios";

const MyAccount = () => {
  const [activeTab, setActiveTab] = useState("account");
  const { parameterState } = useParameters();
  const [parameters, setParameters] = useState([]);
  const [parametersSubmitting, setParametersSubmitting] = useState(false);

  const [upgradeStarting, setUpgradeStarting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [accountInfo, setAccountInfo] = useState({
    username: "",
    created: "",
    accountId: "",
    plan: "basic",
    packageCancellationPending: false,
    subscriptionCancellationPending: false,
    packageUpgradePending: false,
  });

  const { openModal, closeModal } = useModal();
  const changePasswordModalRef = useRef(null);
  const changeEmailModalRef = useRef(null);
  const { addToast } = useToasts();
  const pictureUploadFieldRef = useRef(null);

  const [report, setReport] = useState({
    firstName: "",
    lastName: "",
    title: "",
    companyName: "",
    phone: "",
    email: "",
    reportFooter: "",
    logoThumbnail: "",
  });
  const [reportErrors, setReportErrors] = useState({});
  const [reportSubmitting, setReportSubmitting] = useState(false);

  useEffect(() => {
    if (window.location.hash === "#subscription") {
      document.getElementById("subscription")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    AccountDataService.GetAccountInfos().then((data) => {
      setAccountInfo({
        username: data.username,
        created: data.created,
        accountId: data.accountId,
        plan: data.isBasic ? "basic" : "pro",
        packageCancellationPending: data.packageCancellationPending,
        packageUpgradePending: data.packageUpgradePending,
        subscriptionCancellationPending: data.subscriptionCancellationPending,
      });

      setReport({
        firstName: data.firstname,
        lastName: data.lastname,
        title: data.title,
        phone: data.phone,
        reportEmail: data.reportEmail,
        reportFooter: data.reportFooter,
        logoThumbnail: data.logoThumbnail,
        companyName: data.companyName,
      });

      setParameters(parameterState ?? []);

      setIsLoading(false);
    });
  }, []);

  const handleParameterChange = (key, value) => {
    setParameters((current) =>
      current.map((parameter) =>
        parameter.key === key
          ? {
              ...parameter,
              value,
            }
          : parameter,
      ),
    );
  };

  const renderParameterInput = (parameter) => {
    const type = parameter.dataType?.toLowerCase();
    const validation = parameter.validation ?? {};

    switch (type) {
      case "boolean":
        return (
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={parameter.value === true}
              onChange={(e) => handleParameterChange(parameter.key, e.target.checked)}
            />
            <span className={styles.slider}></span>
          </label>
        );

      case "integer":
        return (
          <input
            type="number"
            step="1"
            min={validation.min}
            max={validation.max}
            className={styles.input}
            value={parameter.value ?? ""}
            onChange={(e) =>
              handleParameterChange(
                parameter.key,
                e.target.value === "" ? null : parseInt(e.target.value, 10),
              )
            }
          />
        );

      case "decimal":
        return (
          <input
            type="number"
            step="any"
            min={validation.min}
            max={validation.max}
            className={styles.input}
            value={parameter.value ?? ""}
            onChange={(e) =>
              handleParameterChange(
                parameter.key,
                e.target.value === "" ? null : parseFloat(e.target.value),
              )
            }
          />
        );

      case "date":
        return (
          <input
            type="date"
            className={styles.input}
            value={parameter.value ?? ""}
            onChange={(e) => handleParameterChange(parameter.key, e.target.value || null)}
          />
        );

      case "datetime":
        return (
          <input
            type="datetime-local"
            className={styles.input}
            value={parameter.value ?? ""}
            onChange={(e) => handleParameterChange(parameter.key, e.target.value || null)}
          />
        );

      case "select":
        return (
          <select
            className={styles.input}
            value={parameter.value ?? ""}
            onChange={(e) => handleParameterChange(parameter.key, e.target.value)}>
            {(parameter.options ?? []).map((option) => {
              const value = typeof option === "object" ? option.value : option;

              const label =
                typeof option === "object"
                  ? option.labelKey
                    ? i18n.t(option.labelKey)
                    : (option.label ?? option.value)
                  : option;

              return (
                <option key={value} value={value}>
                  {label}
                </option>
              );
            })}
          </select>
        );

      case "multiselect":
        return (
          <select
            multiple
            className={styles.multiSelect}
            value={parameter.value ?? []}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, (option) => option.value);

              handleParameterChange(parameter.key, values);
            }}>
            {(parameter.options ?? []).map((option) => {
              const value = typeof option === "object" ? option.value : option;

              const label =
                typeof option === "object"
                  ? option.labelKey
                    ? i18n.t(option.labelKey)
                    : (option.label ?? option.value)
                  : option;

              return (
                <option key={value} value={value}>
                  {label}
                </option>
              );
            })}
          </select>
        );

      case "string":
      default:
        return (
          <input
            type="text"
            className={styles.input}
            maxLength={validation.maxLength}
            value={parameter.value ?? ""}
            onChange={(e) => handleParameterChange(parameter.key, e.target.value)}
          />
        );
    }
  };

  const parametersByCategory = parameters.reduce((result, parameter) => {
    const category = parameter.category || "general";

    if (!result[category]) {
      result[category] = [];
    }

    result[category].push(parameter);

    return result;
  }, {});

  const handleParametersSubmit = async (e) => {
    e.preventDefault();

    try {
      setParametersSubmitting(true);

      const values = parameters.map((parameter) => ({
        key: parameter.key,
        value: parameter.value,
      }));

      const result = await AccountDataService.UpdateAccountParameters(values);

      if (result) {
        addToast(i18n.t("MyAccount.Parameters.SaveSuccess"), "PARAMETERSAVED", "SUCCESS", true);
      }
    } catch (ex) {
      console.error(ex);

      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    } finally {
      setParametersSubmitting(false);
    }
  };

  const handleEmailChange = () => {
    openModal({
      title: i18n.t("MyAccount.ChangeEmail.HeaderTitle"),
      form: "",
      wideMode: true,
      hideSaveButton: true,
      hideSaveAndCloseButton: false,
      content: (
        <ChangeUsernameModal ref={changeEmailModalRef} currentEmail={accountInfo.username} />
      ),
      onSave: async () => {
        try {
          if (!changeEmailModalRef.current.isValid()) return;

          await AccountService.ChangeEmail(
            accountInfo.username,
            changeEmailModalRef.current.newEmail(),
          );
          return true;
        } catch (ex) {
          console.log(ex);
          addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
          return false;
        }
      },
      onClose: () => {},
    });
  };

  const handlePasswordChange = () => {
    openModal({
      title: i18n.t("MyAccount.ChangePassword.HeaderTitle"),
      form: "formClient",
      wideMode: true,
      hideSaveButton: true,
      hideSaveAndCloseButton: false,
      content: <ChangePasswordModal ref={changePasswordModalRef} />,
      onSave: async () => {
        try {
          if (changePasswordModalRef.current.isValid()) {
            await AccountService.ChangePassword(
              changePasswordModalRef.current.oldPassword(),
              changePasswordModalRef.current.newPassword(),
            );
            return true;
          } else {
            addToast(i18n.t("MyAccount.ChangePassword.Invalid"), "CHGPSW", "WARNING", true);
            return false;
          }
        } catch (ex) {
          console.log(ex);
          addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
          return false;
        }
      },
      onClose: () => {},
    });
  };

  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReport((r) => ({ ...r, [name]: value }));
  };

  const handleReportFooterChange = (value) => {
    setReport((r) => ({ ...r, reportFooter: value }));
  };

  function validateReport() {
    const errs = {};
    //if (!report.firstName?.trim()) errs.firstName = i18n.t("Generic.Required");
    //if (!report.lastName?.trim()) errs.lastName = i18n.t("Generic.Required");
    //if (!report.title?.trim()) errs.title = i18n.t("Generic.Required");
    //if (!report.phone?.trim()) errs.phone = i18n.t("Generic.Required");
    /*if (!report.reportEmail?.trim()) {
      errs.email = i18n.t("Generic.Required");
    } else {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(report.reportEmail);
      if (!emailOk) errs.email = i18n.t("Generic.EmailInvalid");
    }*/
    setReportErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!validateReport()) return;
    setReportSubmitting(true);
    try {
      const r = { ...report };
      r.logoThumbnail = pictureUploadFieldRef.current.getData().thumbnail;

      await AccountDataService.UpdateAccountReportInfos(r);

      addToast(i18n.t("MyAccount.Report.GenerateSuccess"), "REPORTGEN", "SUCCESS", true);
    } catch (ex) {
      console.error(ex);
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleDowngradeClick = async () => {
    try {
      if (accountInfo.subscriptionCancellationPending) {
        if (!(await AccountService.ResumeSubscription())) {
          return;
        }

        setAccountInfo((prev) => ({
          ...prev,
          packageCancellationPending: false,
        }));
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        await AccountService.CancelSubscription();
        addToast(i18n.t("MyAccount.Subscription.CancelInfo"), "SUBCANCEL", "SUCCESS", true);

        setAccountInfo((prev) => ({
          ...prev,
          packageCancellationPending: true,
        }));
      }
    } catch (ex) {
      console.error(ex);
      addToast(i18n.t("MyAccount.Subscription.Error"), "SUBERROR", "ERROR", true);
    }
  };

  const handleCancelProClick = () => {
    openModal({
      title: i18n.t("MyAccount.Subscription.CancelConfirmTitle"),
      content: (
        <div
          style={{
            marginTop: "120px",
            padding: "16px",
            backgroundColor: "#fff8e5",
            border: "1px solid #f5c26b",
            borderRadius: "6px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}>
          <span style={{ fontSize: "20px" }}>⚠️</span>
          <p
            style={{
              margin: 0,
              fontSize: "1.2rem",
              color: "#7a4f00",
              lineHeight: "1.4",
            }}>
            {i18n.t("MyAccount.Subscription.CancelConfirmMessage")}
          </p>
        </div>
      ),
      dangerMode: true,
      wideMode: false,
      onSave: async () => {
        await handleDowngradeClick();
        closeModal();
      },
    });
  };

  const handleUpgradeClick = async () => {
    if (upgradeStarting) return;

    try {
      setUpgradeStarting(true);

      const locale = navigator.language?.substring(0, 2) || "en";

      const config = ServiceHelper.ApiCallPrevalidateAndGetConfig();

      axios
        .post(`${envConfig.apiBaseUrl}/api/billing/stripe/upgrade-checkout`, { locale }, config)
        .then((response) => {
          const checkoutUrl = response.data?.checkoutUrl;

          if (!checkoutUrl) {
            addToast("Erreur : aucun checkoutUrl reçu du serveur.", "UPGRADEURL", "ERROR", true);
            return;
          }

          window.location.href = checkoutUrl;
        })
        .catch((error) => {
          console.log(error);
          const ok = ServiceHelper.ApiCallPostCatchValidation(error);
          if (ok) return;
          addToast(i18n.t("MyAccount.Subscription.Error"), "UPGRADEERR", "ERROR", true);
          return;
        });
    } catch (ex) {
      console.error(ex);
      addToast(i18n.t("MyAccount.Subscription.Error"), "UPGRADEEX", "ERROR", true);
    } finally {
      setUpgradeStarting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === "account" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("account")}>
          {i18n.t("MyAccount.Tabs.Account")}
        </button>

        <button
          type="button"
          className={`${styles.tab} ${activeTab === "report" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("report")}>
          {i18n.t("MyAccount.Tabs.Report")}
        </button>

        <button
          type="button"
          className={`${styles.tab} ${activeTab === "parameters" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("parameters")}>
          {i18n.t("MyAccount.Tabs.Parameters")}
        </button>
      </div>
      {showModal && <PlanComparisonModal onClose={() => setShowModal(false)} />}

      {activeTab === "account" && (
        <div className={styles.cardRow}>
          <div className={styles.card}>
            <div className={styles.header}>
              <h2 className={styles.subtitle}>{i18n.t("MyAccount.ConnectionInfo.Title")}</h2>
              <div style={{ textAlign: "right" }}>
                <small className={styles.createdAt}>
                  {i18n.t("MyAccount.ConnectionInfo.Resolution", {
                    res: `${window.screen.width} x ${window.screen.height}`,
                  })}
                </small>
                <br />
                <small className={styles.createdAt}>
                  {i18n.t("MyAccount.ConnectionInfo.CreatedOn")}
                  {new Date(accountInfo.created).toLocaleDateString("fr-FR")}
                </small>
              </div>
            </div>

            <div className={styles.section}>
              <label className={styles.label}>{i18n.t("MyAccount.Email.Label")}</label>
              <div className={styles.readOnlyField}>
                <span>{accountInfo.username}</span>
                <button onClick={handleEmailChange} className={styles.button}>
                  {i18n.t("MyAccount.Email.Change")}
                </button>
              </div>
            </div>

            <div className={styles.section}>
              <label className={styles.label}>{i18n.t("MyAccount.Password.Label")}</label>
              <div className={styles.readOnlyField}>
                <span>••••••••</span>
                <button onClick={handlePasswordChange} className={styles.button}>
                  {i18n.t("MyAccount.Password.Change")}
                </button>
              </div>
            </div>
          </div>

          <div className={styles.card} id="subscription">
            <div className={styles.subscriptionHeader}>
              <h2 className={styles.subscriptionTitle}>{i18n.t("MyAccount.Subscription.Title")}</h2>
            </div>

            {/* Promo banner seulement si Basic et pas upgrade en attente */}
            {accountInfo.plan !== "pro" && !accountInfo.packageUpgradePending && (
              <div className={styles.promoBanner}>
                <div className={styles.promoText}>
                  <span className={styles.promoIcon}>💡</span>
                  {i18n.t("MyAccount.Subscription.PromoBanner")}
                </div>
                <p>
                  <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--primary)",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      marginLeft: "0.5rem",
                    }}>
                    Voir la comparaison détaillée
                  </button>
                </p>
              </div>
            )}

            <div className={styles.subscriptionBody}>
              <div className={styles.currentPlanBox}>
                <span className={styles.planLabel}>
                  {i18n.t("MyAccount.Subscription.CurrentPlan")}
                </span>
                <span className={styles.planName}>
                  {accountInfo.plan === "pro"
                    ? i18n.t("MyAccount.Subscription.Professional")
                    : i18n.t("MyAccount.Subscription.Basic")}
                </span>
              </div>

              {/* --- 1. Upgrade pending a toujours priorité --- */}
              {accountInfo.packageUpgradePending && (
                <div className={styles.pendingBox}>
                  <p>{i18n.t("MyAccount.Subscription.UpgradePendingMessage")}</p>
                </div>
              )}

              {/* --- 2. Cancellation pending si pas upgrade pending --- */}
              {!accountInfo.packageUpgradePending &&
                accountInfo.plan === "pro" &&
                (accountInfo.packageCancellationPending ||
                  accountInfo.subscriptionCancellationPending) && (
                  <>
                    <div className={styles.cancelInfo}>
                      {i18n.t(
                        accountInfo.packageCancellationPending
                          ? "MyAccount.Subscription.PackageCancellationPending"
                          : "MyAccount.Subscription.SubscriptionCancellationPending",
                      )}
                    </div>

                    {accountInfo.subscriptionCancellationPending && (
                      <button
                        className={`${styles.cancelButton} ${styles.fullWidth}`}
                        onClick={handleDowngradeClick}>
                        {i18n.t("MyAccount.Subscription.ResumeButton")}
                      </button>
                    )}
                  </>
                )}

              {/* --- 3. Cas Basic (pas d’upgrade en attente) --- */}
              {!accountInfo.packageUpgradePending && accountInfo.plan !== "pro" && (
                <button
                  className={styles.upgradeButton}
                  onClick={handleUpgradeClick}
                  disabled={upgradeStarting}
                  aria-busy={upgradeStarting ? "true" : "false"}>
                  🚀{" "}
                  {upgradeStarting
                    ? i18n.t("Generic.Loading")
                    : i18n.t("MyAccount.Subscription.UpgradeButton")}
                </button>
              )}

              {/* --- 4. Cas Pro normal (pas cancel en attente, pas upgrade en attente) --- */}
              {!accountInfo.packageUpgradePending &&
                accountInfo.plan === "pro" &&
                !accountInfo.packageCancellationPending &&
                !accountInfo.subscriptionCancellationPending && (
                  <button
                    className={`${styles.cancelButton} ${styles.fullWidth}`}
                    onClick={handleCancelProClick}>
                    {i18n.t("MyAccount.Subscription.CancelButton")}
                  </button>
                )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "report" && (
        <div className={`${styles.card} ${styles.cardWide}`}>
          <div className={`${styles.section} ${styles.reportSection}`}>
            <h2 className={styles.subtitle} style={{ marginBottom: "20px" }}>
              {i18n.t("MyAccount.Report.Title")}
            </h2>

            <form className={styles.reportGrid} onSubmit={handleReportSubmit}>
              <div className={styles.formCol}>
                <div className={styles.rowTwoCols}>
                  <div className={styles.inputGroup}>
                    <label className={styles.label} htmlFor="firstName">
                      {i18n.t("MyAccount.Report.FirstName")}
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      className={styles.input}
                      value={report.firstName}
                      onChange={handleReportChange}
                      placeholder={i18n.t("MyAccount.Report.FirstName.Placeholder")}
                    />
                    {reportErrors.firstName && (
                      <small className={styles.error}>{reportErrors.firstName}</small>
                    )}
                  </div>

                  <div className={styles.inputGroup}>
                    <label className={styles.label} htmlFor="lastName">
                      {i18n.t("MyAccount.Report.LastName")}
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      className={styles.input}
                      value={report.lastName}
                      onChange={handleReportChange}
                      placeholder={i18n.t("MyAccount.Report.LastName.Placeholder")}
                    />
                    {reportErrors.lastName && (
                      <small className={styles.error}>{reportErrors.lastName}</small>
                    )}
                  </div>
                </div>

                <div className={styles.formRow}>
                  <label className={styles.label} htmlFor="title">
                    {i18n.t("MyAccount.Report.TitleField")}
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    className={styles.input}
                    value={report.title}
                    onChange={handleReportChange}
                    placeholder={i18n.t("MyAccount.Report.TitleField.Placeholder")}
                  />
                  {reportErrors.title && (
                    <small className={styles.error}>{reportErrors.title}</small>
                  )}
                </div>

                <div className={styles.formRow}>
                  <label className={styles.label} htmlFor="companyName">
                    {i18n.t("MyAccount.Report.CompanyName")}
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    className={styles.input}
                    value={report.companyName}
                    onChange={handleReportChange}
                    placeholder={i18n.t("MyAccount.Report.CompanyName.Placeholder")}
                  />
                </div>

                <div className={styles.formRow}>
                  <label className={styles.label} htmlFor="phone">
                    {i18n.t("MyAccount.Report.Phone")}
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    className={styles.input}
                    value={report.phone}
                    onChange={handleReportChange}
                    placeholder={i18n.t("MyAccount.Report.Phone.Placeholder")}
                  />
                  {reportErrors.phone && (
                    <small className={styles.error}>{reportErrors.phone}</small>
                  )}
                </div>

                <div className={styles.formRow}>
                  <label className={styles.label} htmlFor="email">
                    {i18n.t("MyAccount.Report.Email")}
                  </label>
                  <input
                    id="email"
                    name="reportEmail"
                    type="email"
                    className={styles.input}
                    value={report.reportEmail}
                    onChange={handleReportChange}
                    placeholder={i18n.t("MyAccount.Report.Email.Placeholder")}
                  />
                  {reportErrors.email && (
                    <small className={styles.error}>{reportErrors.email}</small>
                  )}
                </div>

                <div className={styles.formRow}>
                  <label className={styles.label} htmlFor="reportFooter">
                    {i18n.t("MyAccount.Report.ReportFooter")}
                  </label>
                  <RichEditor
                    controlled
                    value={report.reportFooter}
                    onChange={handleReportFooterChange}
                    height={200}
                    showEmoji
                    showTextColor
                    showFontSize
                    showBold
                    showItalic
                    showUnderline
                    showStrikethrough
                  />
                </div>

                <div className={styles.actions}>
                  <button
                    type="submit"
                    className={styles.button}
                    disabled={reportSubmitting}
                    aria-busy={reportSubmitting ? "true" : "false"}>
                    {reportSubmitting
                      ? i18n.t("Modal.DefaultSaveButton")
                      : i18n.t("Modal.DefaultSaveButton")}
                  </button>
                </div>
              </div>

              <div className={styles.imageCol}>
                <label className={styles.label} htmlFor="logo">
                  {i18n.t("MyAccount.Report.Logo")}
                </label>
                <div style={{ width: "100%" }}>
                  <PictureUploadField
                    label=""
                    initFileId={-1}
                    ref={pictureUploadFieldRef}
                    image={report.logoThumbnail}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === "parameters" && (
        <div className={`${styles.card} ${styles.cardWide}`}>
          <div className={styles.section}>
            <h2 className={styles.subtitle}>{i18n.t("MyAccount.Parameters.Title")}</h2>

            <p className={styles.parametersIntro}>{i18n.t("MyAccount.Parameters.Description")}</p>

            <form onSubmit={handleParametersSubmit}>
              {Object.entries(parametersByCategory).map(([category, items]) => (
                <div key={category} className={styles.parameterCategory}>
                  <h3 className={styles.parameterCategoryTitle}>
                    {i18n.t(`MyAccount.Parameters.Categories.${category}`)}
                  </h3>

                  <div className={styles.parameterList}>
                    {items.map((parameter) => (
                      <div key={parameter.key} className={styles.parameterRow}>
                        <div className={styles.parameterInfo}>
                          <label className={styles.parameterLabel}>
                            {i18n.t(parameter.labelKey)}
                          </label>

                          {parameter.descriptionKey && (
                            <div className={styles.parameterDescription}>
                              {i18n.t(parameter.descriptionKey)}
                            </div>
                          )}
                        </div>

                        <div className={styles.parameterControl}>
                          {renderParameterInput(parameter)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {parameters.length === 0 && (
                <div className={styles.noParameters}>{i18n.t("MyAccount.Parameters.Empty")}</div>
              )}

              {parameters.length > 0 && (
                <div className={styles.parametersActions}>
                  <button type="submit" className={styles.button} disabled={parametersSubmitting}>
                    {i18n.t("Modal.DefaultSaveButton")}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {isLoading && (
        <div className={styles.LoadingOverlay}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default MyAccount;
